// Libraries
import PropTypes from 'prop-types';
import { useContext, useEffect, useId, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

// Actions and Selectors
import { selectors as memberSelectors } from 'store/modules/member';
import { selectors as mentorSelectors } from 'store/modules/mentor';
import { actions as authActions, selectors as userSelectors } from 'store/modules/user';

// Components
import { Popover } from 'antd';
import Button from 'components/elements/Button';
import Form from 'components/elements/Form';
import Modal from 'components/elements/Modal';
import PasswordInput from 'components/elements/Form/Input/PasswordInput';
import PasswordChecklist from 'components/elements/PasswordChecklist';

// Dependencies
import { P } from 'components/fontSystem';
import TranslationsContext from 'providers/translationsProvider';
import { useLoginMutation } from 'services/apiSlices/oauth2Api';
import passwordValidator from 'utils/passwordValidator';

// Variables
const CURRENT_PASSWORD = 'current_password';
const INPUT_PASS = 'new_password';
const INPUT_CONFIRM_PASS = 're_new_password';

const CurrentPasswordModal = ({ open = false, onClose, handleRefreshUserData, ...props }) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { t } = useContext(TranslationsContext);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [fieldErrors, setFieldErrors] = useState({
    [CURRENT_PASSWORD]: '',
    [INPUT_PASS]: '',
    [INPUT_CONFIRM_PASS]: '',
  });
  const announceRef = useRef(null);
  const currentPasswordErrorId = useId();
  const newPasswordErrorId = useId();
  const confirmPasswordErrorId = useId();

  useEffect(() => {
    return () => cancelAnimationFrame(announceRef.current);
  }, []);

  const password = Form.useWatch(INPUT_PASS, form);

  const [login] = useLoginMutation();

  const { setPassword } = authActions;

  const user = useSelector(userSelectors.getUser);
  const mentor = useSelector(mentorSelectors.getMentor);
  const member = useSelector(memberSelectors.getMember);
  const userData = member.id ? member : mentor;
  const userAttributes = {
    email: userData.email,
    name: userData.first_name,
    lastname: userData.last_name,
  };

  const handleResetPassword = () => {
    dispatch(
      setPassword({ user: user.username, ...form.getFieldsValue() }, () => {
        login({
          username: user,
          password: form.getFieldsValue()[CURRENT_PASSWORD],
        });

        handleRefreshUserData();
      })
    );
  };

  const ResetPasswordButton = (
    <Button
      type="primary"
      onClick={() => form.submit()}
      disabled={isButtonDisabled}
    >
      {t('Reset Password')}
    </Button>
  );

  const handleFinishFailed = ({ errorFields }) => {
    const next = { [CURRENT_PASSWORD]: '', [INPUT_PASS]: '', [INPUT_CONFIRM_PASS]: '' };
    errorFields.forEach(({ name, errors }) => {
      const fieldName = name[0];
      if (fieldName in next) {
        next[fieldName] = errors[0] || '';
      }
    });
    setFieldErrors(next);

    cancelAnimationFrame(announceRef.current);
    announceRef.current = requestAnimationFrame(() => {
      const firstInvalid = document.querySelector('[aria-invalid="true"]');
      if (firstInvalid) firstInvalid.focus();
    });
  };

  const handleOnValuesChange = () => {
    setIsButtonDisabled(
      !form.isFieldsTouched(true) || form.getFieldsError().some(({ errors }) => errors.length > 0)
    );
  };

  return (
    <StyledModal
      width={600}
      open={open}
      onCancel={onClose}
      title={t('Update your Password')}
      footer={ResetPasswordButton}
      destroyOnClose
      {...props}
    >
      <ModalBody>
        <Form
          form={form}
          onFieldsChange={handleOnValuesChange}
          onFinish={handleResetPassword}
          onFinishFailed={handleFinishFailed}
        >
          <P>{t('Enter your current password in order to set a new one.')}</P>
          <Form.Item
            noAsterisk
            label={t('Current password')}
            size="large"
            name={CURRENT_PASSWORD}
            displayBlock
            rules={[
              {
                required: true,
                message: t('Enter your password here'),
              },
            ]}
          >
            <PasswordInput
              placeholder={t('Enter current password')}
              name={CURRENT_PASSWORD}
              hidePopover
              aria-describedby={currentPasswordErrorId}
              autoComplete="current-password"
            />
          </Form.Item>
          <div id={currentPasswordErrorId} className="a11y-visually-hidden">
            {fieldErrors[CURRENT_PASSWORD]}
          </div>

          <Popover
            content={
              <PasswordChecklist password={password} userAttributes={userAttributes} t={t} />
            }
            trigger="focus"
            getPopupContainer={trigger => trigger.parentElement}
          >
            <Form.Item
              noAsterisk
              label={t('New password')}
              size="large"
              name={INPUT_PASS}
              displayBlock
              rules={[{ validator: passwordValidator(t, userAttributes) }]}
            >
              <PasswordInput
                placeholder={t('Enter new password (min. 10 characters)')}
                name={INPUT_PASS}
                hidePopover
                aria-describedby={newPasswordErrorId}
                autoComplete="new-password"
              />
            </Form.Item>
          </Popover>
          <div id={newPasswordErrorId} className="a11y-visually-hidden">
            {fieldErrors[INPUT_PASS]}
          </div>

          <Form.Item
            noAsterisk
            label={t('Confirm new password')}
            size="large"
            name={INPUT_CONFIRM_PASS}
            dependencies={[INPUT_PASS]} // Validate only if INPUT_PASS changes
            displayBlock
            rules={[
              { required: true, message: t('Enter your password here') },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue(INPUT_PASS) === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error(t('Passwords do not match!')));
                },
              }),
            ]}
          >
            <PasswordInput
              placeholder={t('Enter new password again')}
              name={INPUT_PASS}
              hidePopover
              aria-describedby={confirmPasswordErrorId}
              autoComplete="new-password"
            />
          </Form.Item>
          <div id={confirmPasswordErrorId} className="a11y-visually-hidden">
            {fieldErrors[INPUT_CONFIRM_PASS]}
          </div>
        </Form>
      </ModalBody>
    </StyledModal>
  );
};

CurrentPasswordModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  handleRefreshUserData: PropTypes.func.isRequired,
};

const StyledModal = styled(Modal)`
  .ant-modal-header {
    padding-bottom: 0;
  }
  .ant-modal-body {
    padding-top: 3px;
  }
`;
const ModalBody = styled.div`
  p {
    padding-bottom: 30px;
  }
`;

export default CurrentPasswordModal;
